import * as styleConstants from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { ContentWrapper } from "@affinity/Overrides/Widgets/Basic/RichContent";
import Icon from "@insite/mobius/Icon";
import * as React from "react";
import styled from "styled-components";
import { css } from "styled-components";

export interface CardProps {
    color: "darkBlue" | "green" | "lightBlue";
    variant: "outline" | "filled" | "header";
    title?: string;
    icon?: string;
    header?: React.ReactNode;
    as?: React.ElementType;
}

export const Card: React.FC<CardProps> = ({ color, variant, title, icon, header, as, children }) => {
    return (
        <CardWrap color={color} variant={variant} as={as}>
            {title && (
                <CardHeader color={color}>
                    {icon && <Icon src={icon} size={24} color="currentColor" />}
                    <CardTitle>{title}</CardTitle>
                </CardHeader>
            )}
            {header || null}
            <CardContent variant={variant}>{children}</CardContent>
        </CardWrap>
    );
};

const CardWrap = styled.div<Pick<CardProps, "color" | "variant">>`
    position: relative;
    width: 100%;
    height: auto;
    display: block;
    overflow: hidden;
    border-radius: 15px;
    ${({ color, variant }) => {
        if (variant !== "header") {
            return css`
                border: 3px solid ${styleConstants.colours[color]};
                border-bottom-width: 5px;
                @media screen and (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    border-bottom-width: 10px;
                }
            `;
        }
    }}
    ${({ color, variant }) => {
        if (variant === "filled") {
            return css`
                background-color: ${styleConstants.colours[color]};
                color: ${styleConstants.colours.white};
            `;
        }
        if (variant === "header") {
            return css`
                background-color: ${styleHelpers.tint(styleConstants.colours[color], 0.8)};
            `;
        }
        return css`
            background-color: ${styleConstants.colours.white};
        `;
    }}
`;

const CardHeader = styled.div<Pick<CardProps, "color">>`
    display: flex;
    align-items: center;
    gap: ${styleHelpers.rem(12)};
    font-family: ${styleConstants.headerFont};
    line-height: 1.25em;
    background-color: ${({ color }) => {
        return styleConstants.colours[color];
    }};
    color: ${styleConstants.colours.white};
    font-size: ${styleHelpers.rem(20)};
    padding: ${styleHelpers.rem(15)} ${styleHelpers.rem(20)};
    @media screen and (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        font-size: ${styleHelpers.rem(24)};
        padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(30)};
    }
`;

const CardTitle = styled.span`
    ${styleConstants.headerMargins}
`;

export const CardContent = styled.div<Pick<CardProps, "variant">>`
    padding: ${styleHelpers.rem(15)} ${styleHelpers.rem(20)};
    @media screen and (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(30)};
    }
    ${({ variant }) => {
        if (variant === "filled") {
            return css`
                color: ${styleConstants.colours.white};
                ${ContentWrapper} {
                    color: ${styleConstants.colours.white};
                }
                padding-top: 0 !important;
            `;
        }
    }}
`;
